/**
 * Per-call options for an analytics event (matches `@kit/analytics`).
 *
 * `sourceApp` is typed as a plain string here rather than importing
 * `@kit/analytics`'s `SourceApp` union — this package is a dependency *of*
 * `@kit/analytics`, so importing back would be circular. The union is enforced at
 * the call site.
 */
export interface AnalyticsEventOptions {
  distinctId?: string;
  groups?: Record<string, string>;
  sourceApp?: string;
}

/** PostHog property name for the client-application dimension. */
export const SOURCE_APP_PROPERTY = 'source_app';

/**
 * Generic PostHog analytics service interface.
 */
export interface PostHogImplementation {
  initialize(): Promise<void>;
  identify(userId: string, traits?: Record<string, string>): Promise<void>;
  group(
    groupType: string,
    groupKey: string,
    groupProperties?: Record<string, string>,
  ): Promise<void>;
  trackPageView(url: string): Promise<void>;
  trackEvent(
    eventName: string,
    eventProperties?: Record<string, string | string[]>,
    options?: AnalyticsEventOptions,
  ): Promise<void>;
  registerProperties(properties: Record<string, string>): Promise<void>;
  unregisterProperty(key: string): Promise<void>;
  log(...args: unknown[]): void;
}

/**
 * PostHog analytics service that sends events to PostHog.
 */
export class PostHogAnalyticsService {
  constructor(
    private client: PostHogImplementation,
    private posthogKey: string,
    private posthogHost: string,
  ) {}

  async initialize() {
    this.log('Initializing PostHog analytics service');

    if (!this.posthogKey || !this.posthogHost) {
      this.log('PostHog key or host not provided, skipping initialization');
      return;
    }

    return this.client.initialize();
  }

  async identify(userId: string, traits?: Record<string, string>) {
    this.log(`Identifying user ${userId} with traits:`, traits);

    return this.client.identify(userId, traits);
  }

  async group(
    groupType: string,
    groupKey: string,
    groupProperties?: Record<string, string>,
  ) {
    this.log(`Associating group ${groupType}:${groupKey}`, groupProperties);

    return this.client.group(groupType, groupKey, groupProperties);
  }

  async trackPageView(url: string) {
    this.log(`Tracking page view for URL: ${url}`);

    return this.client.trackPageView(url);
  }

  async trackEvent(
    eventName: string,
    eventProperties?: Record<string, string | string[]>,
    options?: AnalyticsEventOptions,
  ) {
    this.log(`Tracking event ${eventName} with properties:`, eventProperties);

    return this.client.trackEvent(eventName, eventProperties, options);
  }

  async registerProperties(properties: Record<string, string>) {
    this.log('Registering super properties:', properties);

    return this.client.registerProperties(properties);
  }

  async unregisterProperty(key: string) {
    this.log(`Unregistering super property: ${key}`);

    return this.client.unregisterProperty(key);
  }

  private log(...args: unknown[]) {
    this.client.log(...args);
  }
}
